import behaviourStudentListConstants from "@/constants/behaviourStudentListConstants.js"
import {
  buildPointHistoryTransactionsParams,
  mapApiTransactionToRow,
  normalizePointHistoryGradYearValues,
  parseStudentPointsSummaryResponse
} from "@/helpers/behaviourPointHistoryApi.js"

/** Allowed values for the student list `sort` query param. */
export const STUDENT_LIST_SORT_FIELDS = Object.freeze(["remaining_points"])

/** Allowed values for the student list `order` query param. */
export const STUDENT_LIST_SORT_ORDERS = Object.freeze(["asc", "desc"])

/**
 * Maps a table column key to the API `sort` value.
 * @param {string|null|undefined} columnKey
 * @returns {string|null}
 */
export function mapStudentListSortKey(columnKey) {
  if (String(columnKey ?? "").trim() === "totalPoints") {
    return "remaining_points"
  }
  return null
}

/**
 * @param {{
 *   page?: number,
 *   perPage?: number,
 *   search?: string,
 *   gradYears?: unknown,
 *   sortBy?: string|null,
 *   sortOrder?: string|null
 * }} args
 * @returns {Record<string, string | number>}
 */
export function buildStudentListParams({
  page = 1,
  perPage = 25,
  search = "",
  gradYears = [],
  sortBy = null,
  sortOrder = null
} = {}) {
  const params = {
    page: Math.max(1, Number(page) || 1),
    per_page: Math.max(1, Number(perPage) || 25)
  }

  const q = String(search ?? "").trim()
  if (q) {
    params.search = q
  }

  const gradYearValues = normalizePointHistoryGradYearValues(gradYears)
  gradYearValues.forEach((year, index) => {
    params[`grad_year[${index}]`] = year
  })

  const sortByValue = String(sortBy ?? "").trim()
  const sortOrderValue = String(sortOrder ?? "")
    .trim()
    .toLowerCase()
  if (
    STUDENT_LIST_SORT_FIELDS.includes(sortByValue) &&
    STUDENT_LIST_SORT_ORDERS.includes(sortOrderValue)
  ) {
    params.sort = sortByValue
    params.order = sortOrderValue
  }

  return params
}

/**
 * Same filter and pagination params as the student list.
 * @param {Parameters<typeof buildStudentListParams>[0]} args
 * @returns {Record<string, string | number>}
 */
export function buildStudentListExportParams(args = {}) {
  return buildStudentListParams(args)
}

/**
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function mapApiStudentListRow(item) {
  const studentId = item?.student_id ?? item?.id ?? null

  return {
    id: studentId,
    studentId,
    studentName: String(item?.student_name ?? "").trim(),
    studentNumber: String(item?.student_number ?? "").trim(),
    email: String(item?.email ?? "").trim(),
    gradYear: item?.grad_year ?? item?.gradYear ?? null,
    totalPoints: Number(item?.total_points ?? item?.remaining_points ?? 0) || 0
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{ rows: Record<string, unknown>[], pagination: Record<string, unknown> }}
 */
export function parseStudentListResponse(body) {
  const rows = (body?.data ?? []).map(mapApiStudentListRow)

  return {
    rows,
    pagination: body?.pagination || body?.meta || {}
  }
}

/**
 * @param {number | string} studentId
 * @param {{
 *   fromDate?: string,
 *   toDate?: string,
 *   page?: number,
 *   perPage?: number,
 *   search?: string,
 *   coreValueId?: string | number | null,
 *   sortBy?: string|null,
 *   sortOrder?: string|null
 * }} args
 * @returns {Record<string, string | number>}
 */
export function buildStaffStudentTransactionsParams(
  _studentId,
  {
    fromDate = "",
    toDate = "",
    page = 1,
    perPage = 25,
    search = "",
    coreValueId = null,
    sortBy = null,
    sortOrder = null
  } = {}
) {
  return buildPointHistoryTransactionsParams({
    fromDate,
    toDate,
    page,
    perPage,
    search,
    coreValueId,
    sortBy,
    sortOrder
  })
}

/**
 * Same filter and sort params as the staff student transactions list.
 * @param {number | string} studentId
 * @param {Parameters<typeof buildStaffStudentTransactionsParams>[1]} args
 * @returns {Record<string, string | number>}
 */
export function buildStaffStudentTransactionsExportParams(
  studentId,
  args = {}
) {
  return buildStaffStudentTransactionsParams(studentId, args)
}

/**
 * @param {number | string} studentId
 * @param {{ fromDate?: string, toDate?: string }} args
 * @returns {Record<string, string>}
 */
export function buildStaffStudentSummaryParams(
  studentId,
  { fromDate = "", toDate = "" } = {}
) {
  const params = {}

  const from = String(fromDate ?? "").trim()
  const to = String(toDate ?? "").trim()

  if (from) {
    params.from_date = from
  }
  if (to) {
    params.to_date = to
  }

  return params
}

/**
 * @param {number | string} studentId
 * @returns {string}
 */
export function staffStudentSummaryPath(studentId) {
  const id = Number(studentId)
  if (!Number.isFinite(id) || id <= 0) {
    return ""
  }
  return `${behaviourStudentListConstants.STAFF_STUDENT_POINTS_SUMMARY_API_PATH}/${id}${behaviourStudentListConstants.STAFF_STUDENT_POINTS_SUMMARY_SUFFIX}`
}

/**
 * @param {number | string} studentId
 * @returns {string}
 */
export function staffStudentTransactionsPath(studentId) {
  const id = Number(studentId)
  if (!Number.isFinite(id) || id <= 0) {
    return ""
  }
  return `${behaviourStudentListConstants.STAFF_STUDENT_POINTS_SUMMARY_API_PATH}/${id}${behaviourStudentListConstants.STAFF_STUDENT_POINTS_TRANSACTIONS_SUFFIX}`
}

/**
 * @param {number | string} studentId
 * @returns {string}
 */
export function staffStudentTransactionsExportPath(studentId) {
  const id = Number(studentId)
  if (!Number.isFinite(id) || id <= 0) {
    return ""
  }
  return `${behaviourStudentListConstants.STAFF_STUDENT_POINTS_SUMMARY_API_PATH}/${id}${behaviourStudentListConstants.STAFF_STUDENT_POINTS_TRANSACTIONS_EXPORT_SUFFIX}`
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{ rows: Record<string, unknown>[], pagination: Record<string, unknown> }}
 */
export function parseStaffStudentTransactionsResponse(body) {
  const rows = (body?.data ?? []).map(mapApiTransactionToRow)

  return {
    rows,
    pagination: body?.pagination || body?.meta || {}
  }
}

export { parseStudentPointsSummaryResponse }
